import { MouseEvent, useEffect, useMemo, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Stack, IconButton, Tooltip, Typography, Grid, Dialog } from '@mui/material';
// third-party
import { Row } from 'react-table';
// project-imports
import ScrollX from 'components/ScrollX';
import MainCard from 'components/MainCard';
import serverCall from 'api/serverCall';
import api from 'api/api';
import { Edit, Trash } from 'iconsax-react';
import { ThemeMode } from 'types/config';
import { formatDate } from 'utils/formatters';
import ShimmerTable from 'shimmers/components/TableShimmer';
import { ConfigsTable } from './components/ConfigsTable';
import { IFund, ISector } from 'types/custom/common';
import { PopupTransition } from 'components/@extended/Transitions';
import AddConfig from './components/AddConfig';
import DeleteConfirmationAlert from 'components/alert/DeleteConfirmationAlert';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { useFirebaseContext } from 'contexts/FirebaseContext';

type Props = {};

function StockGroupPage({}: Props) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [sectors, setSectors] = useState<ISector[]>([]);
  const [funds, setFunds] = useState<IFund[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [type, setType] = useState<'sector' | 'fund' | null>(null);
  const [currentData, setCurrentData] = useState<IFund | ISector | null>(null);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [isSave, setIsSave] = useState<boolean>(false);
  const { user } = useFirebaseContext();
  const permissions = user?.permissions;
  const hasEditPermission = permissions?.includes('canUpdateConfig');
  const handleOpenDeleteModal = () => {
    setIsDelete((prev) => !prev);
  };

  const fetchAllFunds = (): Promise<IFund[]> => {
    return new Promise((resolve, reject) => {
      serverCall.get(api.getAllFund(), (response, err) => {
        if (err) {
          reject(err);
        } else {
          resolve(response);
        }
      });
    });
  };

  const fetchAllSectors = (): Promise<ISector[]> => {
    return new Promise((resolve, reject) => {
      serverCall.get(api.getAllSectors(), (response, err) => {
        if (err) {
          reject(err);
        } else {
          resolve(response);
        }
      });
    });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sectorResult, fundResult] = await Promise.allSettled([fetchAllSectors(), fetchAllFunds()]);
      if (sectorResult.status === 'fulfilled') {
        setSectors(sectorResult.value);
      }
      if (fundResult.status === 'fulfilled') {
        setFunds(fundResult.value);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = () => {
    let deleteId = null;
    let url = null;
    if (type === 'sector') {
      deleteId = (currentData as ISector).sectorId;
      url = api.deleteSector(deleteId);
    } else {
      deleteId = (currentData as IFund).fundId;
      url = api.deleteFund(deleteId);
    }
    serverCall.delete(url, (response, error) => {
      if (error) {
        console.error(error);
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Failed to delete`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right'
            }
          })
        );
      } else {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Successfully deleted`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right'
            }
          })
        );
        setIsDelete(false);
        setCurrentData(null);
        fetchData();
      }
    });
  };

  const sectorColumns = useMemo(
    () => [
      {
        Header: '#',
        className: 'cell-center',
        Cell: ({ row }: { row: Row }) => {
          return <>{row.index + 1}</>;
        }
      },
      {
        Header: 'Sector Name',
        className: 'cell-center',
        accessor: 'sectorName'
      },
      {
        Header: 'Description',
        accessor: 'description',
        disableSortBy: true
      },
      {
        Header: 'CreatedOn',
        accessor: 'createdOn',
        Cell: ({ row }: { row: Row }) => {
          const sector = row.original as ISector;
          return <>{formatDate(sector.createdOn)}</>;
        }
      },
      {
        Header: 'Actions',
        disableSortBy: true,
        Cell: ({ row }: { row: Row<{}> }) => {
          return (
            <>
              {hasEditPermission && (
                <Stack direction="row" alignItems="center" justifyContent="start" spacing={0}>
                  <Tooltip
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                          opacity: 0.9
                        }
                      }
                    }}
                    title="Edit"
                  >
                    <IconButton
                      color="primary"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        const sector = row.original as ISector;
                        setType('sector');
                        setCurrentData(sector);
                        setIsSave(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                          opacity: 0.9
                        }
                      }
                    }}
                    title="Delete"
                  >
                    <IconButton
                      color="error"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        const sector = row.original as ISector;
                        setType('sector');
                        setCurrentData(sector);
                        e.stopPropagation();
                        handleOpenDeleteModal();
                      }}
                    >
                      <Trash />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </>
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme]
  );

  const fundColumns = useMemo(
    () => [
      {
        Header: '#',
        className: 'cell-center',
        Cell: ({ row }: { row: Row }) => {
          return <>{row.index + 1}</>;
        }
      },
      {
        Header: 'Fund Name',
        className: 'cell-center',
        accessor: 'fundName'
      },
      {
        Header: 'Description',
        accessor: 'description',
        disableSortBy: true
      },
      {
        Header: 'CreatedOn',
        accessor: 'createdOn',
        Cell: ({ row }: { row: Row }) => {
          const fund = row.original as IFund;
          return <>{formatDate(fund.createdOn)}</>;
        }
      },
      {
        Header: 'Actions',
        disableSortBy: true,
        Cell: ({ row }: { row: Row<{}> }) => {
          return (
            <>
              {hasEditPermission && (
                <Stack direction="row" alignItems="center" justifyContent="start" spacing={0}>
                  <Tooltip
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                          opacity: 0.9
                        }
                      }
                    }}
                    title="Edit"
                  >
                    <IconButton
                      color="primary"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        const fund = row.original as IFund;
                        setType('fund');
                        setCurrentData(fund);
                        setIsSave(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[50] : theme.palette.grey[700],
                          opacity: 0.9
                        }
                      }
                    }}
                    title="Delete"
                  >
                    <IconButton
                      color="error"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        const fund = row.original as IFund;
                        e.stopPropagation();
                        setType('fund');
                        setCurrentData(fund);
                        handleOpenDeleteModal();
                      }}
                    >
                      <Trash />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </>
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme]
  );

  const handleCancel = () => {
    setIsSave(false);
    setType(null);
    setCurrentData(null);
  };

  if (loading && sectors.length === 0 && funds.length === 0) {
    return <ShimmerTable columns={10} rows={15} />;
  }

  return (
    <Stack>
      {isSave && (
        <Dialog
          maxWidth="sm"
          fullWidth
          TransitionComponent={PopupTransition}
          onClose={handleCancel}
          open={isSave}
          sx={{ '& .MuiDialog-paper': { p: 0 } }}
        >
          <AddConfig fetchData={fetchData} data={currentData!} onCancel={handleCancel} type={type} />
        </Dialog>
      )}
      {isDelete && (
        <DeleteConfirmationAlert
          handleClose={setIsDelete}
          open={isDelete}
          content={
            <Stack spacing={2}>
              <Typography variant="h4" align="center">
                Are you sure you want to delete?
              </Typography>
              <Typography align="center">
                By deleting
                <Typography variant="subtitle1" component="span">
                  "{type === 'sector' ? (currentData as ISector).sectorName : (currentData as IFund).fundName}"
                </Typography>
                all associated data will be permanently removed.
              </Typography>
            </Stack>
          }
          handleDelete={handleDelete}
        />
      )}

      <Grid>
        <Grid>
          <Typography sx={{ mb: 2 }} variant="h3">
            Sector List
          </Typography>
        </Grid>
        <MainCard content={false}>
          <ScrollX>
            <ConfigsTable
              columns={sectorColumns}
              data={sectors ?? []}
              emptyMessage="No Sectors found, Create a sector!"
              type={'sector'}
              setIsSave={setIsSave}
              setType={setType}
            />
          </ScrollX>
        </MainCard>
      </Grid>
      <Grid sx={{ mt: 5 }}>
        <Grid>
          <Typography sx={{ mb: 2 }} variant="h3">
            Fund List
          </Typography>
        </Grid>
        <MainCard content={false}>
          <ScrollX>
            <ConfigsTable
              type={'fund'}
              emptyMessage="No Funds found, Create a fund!"
              setIsSave={setIsSave}
              columns={fundColumns}
              data={funds ?? []}
              setType={setType}
            />
          </ScrollX>
        </MainCard>
      </Grid>
    </Stack>
  );
}

export default StockGroupPage;
